import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  useTracks,
  VideoTrack,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useCart } from '../context/CartContext';

const LIVEKIT_URL = 'wss://cherrypkd-cu73p5yu.livekit.cloud';
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000';

// ── Live auction countdown ─────────────────────────────────────────
function AuctionCountdown({ endsAt }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const tick = () => {
      const diff = new Date(endsAt) - new Date();
      if (diff <= 0) { setTimeLeft('Ended'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [endsAt]);

  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
      <span style={{ fontSize:12 }}>⏰</span>
      <span style={{ color:'#FF8A65', fontWeight:700, fontSize:13 }}>{timeLeft} left</span>
    </div>
  );
}

// ── Featured item action card — changes by listing type ────────────
function FeaturedCTA({ featured, user, navigate, addToCart, cartMsg, setCartMsg }) {
  const type = featured.listing_type || featured.featured_type || 'sale';

  if (type === 'auction') {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4, minWidth:120 }}>
        {featured.auction_ends_at && <AuctionCountdown endsAt={featured.auction_ends_at} />}
        <div style={{ color:'#4CAF50', fontWeight:800, fontSize:16, marginBottom:4 }}>
          ${parseFloat(featured.price || featured.starting_bid || 0).toLocaleString()}
          <span style={{ fontSize:11, opacity:.6, fontWeight:400, marginLeft:4 }}>current bid</span>
        </div>
        <button
          onClick={() => navigate(`/listing/${featured.slug || featured.id}`)}
          style={{ background:'#FF8A65', border:'none', color:'white', borderRadius:8, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
          🔨 Place Bid
        </button>
        <button
          onClick={() => navigate(`/listing/${featured.slug || featured.id}`)}
          style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', borderRadius:8, padding:'6px 10px', fontSize:12, cursor:'pointer' }}>
          View Auction
        </button>
      </div>
    );
  }

  if (type === 'barter') {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:120 }}>
        <div style={{ fontSize:11, color:'rgba(255,255,255,.5)', marginBottom:2 }}>Barter item</div>
        <button
          onClick={() => navigate(`/barter/${featured.id}`)}
          style={{ background:'#9C27B0', border:'none', color:'white', borderRadius:8, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
          🔄 Propose Barter
        </button>
      </div>
    );
  }

  if (type === 'rent') {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:120 }}>
        <div style={{ color:'#4CAF50', fontWeight:800, fontSize:16, marginBottom:2 }}>
          ${parseFloat(featured.price || 0).toLocaleString()}<span style={{ fontSize:11, opacity:.6, fontWeight:400 }}>/day</span>
        </div>
        <button
          onClick={() => navigate(`/listing/${featured.slug || featured.id}`)}
          style={{ background:'#2196F3', border:'none', color:'white', borderRadius:8, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
          📅 Book Now
        </button>
      </div>
    );
  }

  if (type === 'free') {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:120 }}>
        <div style={{ color:'#4CAF50', fontWeight:800, fontSize:16, marginBottom:2 }}>FREE</div>
        <button
          onClick={() => navigate(`/listing/${featured.slug || featured.id}`)}
          style={{ background:'#4CAF50', border:'none', color:'white', borderRadius:8, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
          🆓 Claim Item
        </button>
      </div>
    );
  }

  // Default: sale
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:120 }}>
      <div style={{ color:'#4CAF50', fontWeight:800, fontSize:20, marginBottom:2 }}>
        ${parseFloat(featured.price || 0).toLocaleString()}
      </div>
      <button
        onClick={() => {
          if (!user) { navigate('/login'); return; }
          addToCart({ ...featured, listing_type: 'sale' });
          setCartMsg('Added to cart!');
          setTimeout(() => setCartMsg(''), 3000);
        }}
        style={{ background:'#4CAF50', border:'none', color:'white', borderRadius:8, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
        {cartMsg || '🛒 Buy Now'}
      </button>
      <button
        onClick={() => navigate(`/listing/${featured.slug || featured.id}`)}
        style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', borderRadius:8, padding:'6px 10px', fontSize:12, cursor:'pointer' }}>
        View Listing
      </button>
    </div>
  );
}

// ── Viewer inner component — inside LiveKitRoom ────────────────────
function ViewerScreen({ session: initialSession, socket, roomName }) {
  const { user }    = useAuth();
  const { addToCart } = useCart();
  const navigate    = useNavigate();
  const tracks      = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const hostTrack   = tracks.find(t => !t.participant?.isLocal);

  const [session, setSession]   = useState(initialSession);
  const [featured, setFeatured] = useState(
    initialSession.featured_id ? {
      id: initialSession.featured_id,
      title: initialSession.featured_title,
      price: initialSession.featured_price,
      condition: initialSession.featured_condition,
      primary_image: initialSession.featured_image,
      listing_type: initialSession.featured_type,
      auction_ends_at: initialSession.featured_auction_ends_at,
      starting_bid: initialSession.featured_starting_bid,
    } : null
  );
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput]       = useState('');
  const [viewerCount, setViewerCount]   = useState(0);
  const [ended, setEnded]               = useState(false);
  const [cartMsg, setCartMsg]           = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join_live', roomName);
    socket.on('live_chat',         msg  => setChatMessages(p => [...p.slice(-199), msg]));
    socket.on('live_feature_item', item => setFeatured(item));
    socket.on('live_viewer_count', cnt  => setViewerCount(cnt));
    socket.on('live_ended',        ()   => setEnded(true));
    return () => {
      socket.off('live_chat');
      socket.off('live_feature_item');
      socket.off('live_viewer_count');
      socket.off('live_ended');
      socket.emit('leave_live', roomName);
    };
  }, [socket, roomName]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendChat = useCallback(() => {
    if (!chatInput.trim() || !user) return;
    socket?.emit('live_chat', {
      roomName,
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      message: chatInput.trim(),
      avatar: user.avatar_url,
    });
    setChatInput('');
  }, [chatInput, roomName, socket, user]);

  if (ended) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0a', color:'white', flexDirection:'column', gap:16 }}>
        <div style={{ fontSize:56 }}>📺</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700 }}>Stream Ended</h2>
        <p style={{ opacity:.6 }}>The seller has ended this live stream.</p>
        <button className="btn btn-cherry" onClick={() => navigate('/browse')}>Keep Browsing</button>
      </div>
    );
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', height:'100vh', background:'#0a0a0a', color:'white', overflow:'hidden' }}>
      {/* Left — Video */}
      <div style={{ display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 20px', background:'rgba(255,255,255,.06)' }}>
          <div style={{ background:'#E53935', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:800, letterSpacing:'1px' }}>● LIVE</div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {session.avatar_url && <img src={`${API_BASE}${session.avatar_url}`} alt="" style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />}
            <span style={{ fontWeight:700, fontSize:14 }}>{session.first_name} {session.last_name}</span>
          </div>
          <div style={{ fontWeight:400, fontSize:13, opacity:.6 }}>{session.title}</div>
          <div style={{ marginLeft:'auto', fontSize:13, opacity:.6 }}>👥 {viewerCount}</div>
        </div>

        {/* Video */}
        <div style={{ flex:1, position:'relative', background:'#111' }}>
          {hostTrack
            ? <VideoTrack trackRef={hostTrack} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:12, opacity:.4 }}>
                <div style={{ fontSize:48 }}>📡</div>
                <div style={{ fontSize:14 }}>Connecting to stream...</div>
              </div>
          }

          {/* Featured item card — type-aware */}
          {featured && (
            <div style={{ position:'absolute', bottom:20, left:20, background:'rgba(0,0,0,.9)', borderRadius:14, padding:'14px 18px', display:'flex', gap:14, alignItems:'center', maxWidth:380, backdropFilter:'blur(8px)' }}>
              {featured.primary_image && (
                <img src={`${API_BASE}${featured.primary_image}`} alt="" style={{ width:64, height:64, borderRadius:10, objectFit:'cover', flexShrink:0 }} />
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:11, opacity:.5, marginBottom:2, textTransform:'uppercase', letterSpacing:'1px' }}>
                  {featured.listing_type === 'auction' ? '🔨 Live Auction' :
                   featured.listing_type === 'barter'  ? '🔄 Barter Offer' :
                   featured.listing_type === 'rent'    ? '📅 For Rent' :
                   featured.listing_type === 'free'    ? '🆓 Free Item' :
                   'Featured Item'}
                </div>
                <div style={{ fontWeight:700, fontSize:14, lineHeight:1.3, marginBottom:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{featured.title}</div>
              </div>
              <FeaturedCTA
                featured={featured}
                user={user}
                navigate={navigate}
                addToCart={addToCart}
                cartMsg={cartMsg}
                setCartMsg={setCartMsg}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right — Chat */}
      <div style={{ display:'flex', flexDirection:'column', borderLeft:'1px solid rgba(255,255,255,.1)', background:'#111' }}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,.1)', fontWeight:700, fontSize:14 }}>
          💬 Live Chat
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'12px', display:'flex', flexDirection:'column', gap:8 }}>
          {chatMessages.length === 0 && (
            <div style={{ textAlign:'center', opacity:.3, fontSize:13, marginTop:40 }}>
              {user ? 'Be the first to say hi! 👋' : 'Sign in to chat'}
            </div>
          )}
          {chatMessages.map((m, i) => (
            <div key={i} style={{ fontSize:13, lineHeight:1.5 }}>
              <span style={{ fontWeight:700, color: m.userId === session.seller_id ? '#E53935' : '#FF8A65' }}>
                {m.userId === session.seller_id ? '🎙️ ' : ''}{m.userName}:
              </span>{' '}
              <span style={{ opacity:.85 }}>{m.message}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        {user ? (
          <div style={{ display:'flex', gap:8, padding:12, borderTop:'1px solid rgba(255,255,255,.1)' }}>
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key==='Enter' && sendChat()}
              placeholder="Say something..." style={{ flex:1, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', borderRadius:8, padding:'8px 10px', color:'white', fontSize:13, outline:'none' }} />
            <button onClick={sendChat} style={{ background:'#E53935', border:'none', color:'white', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700 }}>→</button>
          </div>
        ) : (
          <div style={{ padding:12, textAlign:'center', borderTop:'1px solid rgba(255,255,255,.1)' }}>
            <button className="btn btn-cherry btn-sm" onClick={() => navigate('/login')}>Sign in to chat</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page — fetches session + token then renders ViewerScreen ──
export default function LiveWatchPage() {
  const { roomName } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const socket       = useSocket();

  const [session, setSession] = useState(null);
  const [token, setToken]     = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [lkError, setLkError] = useState('');

  useEffect(() => {
    async function init() {
      try {
        const { data: sd } = await API.get(`/live/sessions/${roomName}`);
        setSession(sd.session);
        if (user) {
          const { data: td } = await API.post('/live/token', { roomName, isHost: false });
          setToken(td.token);
        } else {
          const { data: td } = await API.post('/live/token/guest', { roomName }).catch(() => ({ data: { token: '' } }));
          setToken(td.token);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Stream not found or has ended');
      } finally { setLoading(false); }
    }
    init();
  }, [roomName, user]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0a', color:'white', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:40 }}>📡</div>
      <div style={{ fontSize:16, opacity:.6 }}>Joining stream...</div>
    </div>
  );

  if (error) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0a', color:'white', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:48 }}>📺</div>
      <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24 }}>{error}</h2>
      <button className="btn btn-cherry" onClick={() => navigate('/live')}>Browse Live Streams</button>
    </div>
  );

  if (!token) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0a0a0a', color:'white', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:48 }}>🔒</div>
      <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24 }}>Sign in to watch</h2>
      <button className="btn btn-cherry" onClick={() => navigate('/login')}>Sign In</button>
    </div>
  );

  return (
    <>
      {lkError && (
        <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:999, background:'#E53935', color:'white', padding:'12px 20px', fontSize:14, fontWeight:600, display:'flex', alignItems:'center', gap:12 }}>
          <span>⚠️ {lkError}</span>
          <button onClick={() => setLkError('')} style={{ marginLeft:'auto', background:'rgba(255,255,255,.2)', border:'none', color:'white', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12 }}>Dismiss</button>
        </div>
      )}
      <LiveKitRoom
        token={token}
        serverUrl={LIVEKIT_URL}
        connect={true}
        video={false}
        audio={false}
        onError={(err) => {
          console.error('LiveKit error:', err);
          setLkError(err.message || 'Connection error. Check your internet and try again.');
        }}
      >
        <RoomAudioRenderer />
        <ViewerScreen session={session} socket={socket} roomName={roomName} />
      </LiveKitRoom>
    </>
  );
}
